import React, { useState, useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { Layout, Popover, Spin, Select } from "antd";
import { useSelector, useDispatch } from "react-redux";
import AppNotification from "../../components/AppNotification";
import Auxiliary from "util/Auxiliary";
import { ReactComponent as SearchIcon } from "../../assets/images/icon-search.svg";
import { ReactComponent as NotificationIcon } from "../../assets/images/notification-icon.svg";
import { ReactComponent as RefreshIcon } from "../../assets/images/refresh-icon.svg";
import StatusColors from "../Receptionist/Calendar/components/StatusColors";
import { setUserSite, setUserRole } from "../../appRedux/actions/Auth"
import { getRadiscanSession } from "../../services/auth.service";

const { Header } = Layout;
const { Option } = Select;

const Topbar = () => {
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.authUser);
  const location = useLocation();

  const pathname = location.pathname;
  const lastPath = pathname.split("/").filter(Boolean).pop();
  //console.log("Pathname -----", lastPath);
  
  const firstName = authUser?.firstName || "there";
  
  const PAGE_TITLES = {
    dashboard: `Hi ${firstName}, Welcome to Clarity.`,
    calendar: "Calendar",
    visits: "Visits",
    patient_visits: "Visits > Patient Details",
    patients: "Patients",
    appointments: "Appointments",
    worklist: "Worklist",
    lineup: "Lineup",
    organisations: "Organisations",
    ref_doctors: "Referred Doctor",
    aob: "Medicare Assignment of Benefit",
    quick_codes: "Quickcodes",
    accounts: "Accounts",
    profile_settings: "Profile Settings",
    delivery_report: "Delivery Report",
    configuration: "Configuration",
    accounts_legacy: "Accounts Legacy",
    alerts_notifications: "Alerts & Notifications"
  };

  const normalizedPath = lastPath.replace(/-/g, "_");
  const pageTitle = PAGE_TITLES[normalizedPath] || PAGE_TITLES.dashboard;

  const handleSiteChange = (siteId) => {
    const selectedSite = authUser?.mips?.find((m) => m.mipId === siteId);
    dispatch(setUserSite(selectedSite));
    // optionally trigger API refresh here if not already handled by saga/thunk
  };

  const handleRoleChange = async (role) => {
    if (["MIS", "MRT"].includes(role)) {
      setLoading(true);
      try {
        let result = null;
         try {
          result = await getRadiscanSession();
        } catch (error) {
          console.warn("getRadiscanSession() failed or backend not available:", error);
        }

        let redirectUrl = process.env.REACT_APP_RADISCAN_REDIRECT_URL || "";
        if (role === "MRT") {
          redirectUrl = `${redirectUrl}mrt/worklist`;
        } 

        if (result?.success) {
            window.open(
              `${redirectUrl}?email=${authUser.email}&role=${role}`,
              "_blank",
              "noopener,noreferrer"
            );
          } else {
            // Fallback to direct redirect when backend not deployed
            if (redirectUrl) {
              console.info("Using fallback Radiscan redirect (no session API)");
              window.open(
                `${redirectUrl}?email=${authUser.email}&role=${role}`,
                "_blank",
                "noopener,noreferrer"
              );
            } else {
              message.error("Redirect URL not configured in environment.");
            }
          }

      } catch (err) {
        console.error("Unexpected error in Radiscan flow:", err);
      } finally {
        setLoading(false);
      }

      // Reset to fallback role so dropdown doesn’t stay stuck on MIS/MRT
      dispatch(setUserRole(authUser?.role || "RECEPTIONIST"));
    } else {
      dispatch(setUserRole(role));
    }
  };

  //Topbar color status
  const data = [
    { title: "Draft", status: "D", color: "#038fde" },
    { title: "Active", status: "A", color: "#ea954c" },
    { title: "Confirmed", status: "A", color: "#91c9a1" },
    { title: "Attended", status: "T", color: "rgb(5 147 102)" },
    { title: "Did Not Attend", status: "N", color: "#CE8FCE" },
    { title: "Reserve", status: "N", color: "#E9967A" },
    { title: "Warning", status: "W", color: "#FFD700" },
  ];

  // WebSocket connect
  const socket = useRef(null);
  const selectedSiteIdRef = useRef(authUser?.site?.mipId);
  
  useEffect(() => {
    selectedSiteIdRef.current = authUser?.site?.mipId;

    let reconnectTimeout = null;

    const connectWebSocket = () => {
      // Prevent multiple connection attempts
      if (socket.current && socket.current.readyState < 3) return;

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const websocketUrl = `${protocol}//${window.location.host}/ws`;

      console.log("Connecting to WebSocket:", websocketUrl);

      socket.current = new WebSocket(websocketUrl, []);

      socket.current.onopen = () => {
        console.log("WebSocket connected");
      };

      socket.current.onmessage = (event) => {
        const mipIdFromEvent = Number(event?.data);

        // Only dispatch the event if the message's mipId matches the currently selected site.
        if (mipIdFromEvent > 0 && mipIdFromEvent === selectedSiteIdRef.current) {
          window.dispatchEvent(new CustomEvent("ws-refresh-appointments"));
        }
      };

      socket.current.onerror = (error) => {
        console.error("WebSocket error:", error);
      };

      socket.current.onclose = (event) => {
        console.log("WebSocket disconnected", event);

        // Auto reconnect after 5 seconds
        reconnectTimeout = setTimeout(() => {
          connectWebSocket();
        }, 5000);
      };
    };

    connectWebSocket();

    return () => {
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }

      if (socket.current) {
        socket.current.close();
        socket.current = null;
      }
    };
  }, [authUser?.site?.mipId]);

  return (
    <Auxiliary>
      <Header>
        <ul className="gx-header-notifications">
          <li className="gx-title">
            <h1>{pageTitle}</h1>
          </li>

          {/* Right Part */}
          <div
            className="gx-d-flex gx-align-items-center"
            style={{ gap: "12px" }}
          >
            {/* <li className="gx-notify gx-notify-search gx-d-inline-block">
              <Popover
                overlayClassName="gx-popover-horizantal"
                placement="bottomRight"
                trigger="click"
              >
                <span className="gx-pointer gx-d-block">
                  <SearchIcon />
                </span>
              </Popover>
            </li> */}

            <li className="gx-notify">
              <Popover
                overlayClassName="gx-popover-horizantal"
                placement="bottomRight"
                content={<AppNotification />}
                trigger="click"
              >
                <span className="gx-pointer gx-d-block">
                  <NotificationIcon />
                </span>
              </Popover>
            </li>

            {/* <li className="gx-refresh">
              <Button type="link">
                <RefreshIcon />
              </Button>
            </li> */}
            {/* Site Dropdown */}
            <li>
              <Select
                value={authUser?.site?.mipId}
                onChange={handleSiteChange}
                style={{ width: 200 }}
                placeholder="Select Site"
              >
                {authUser?.mips?.map((mip) => (
                  <Option key={mip.mipId} value={mip.mipId}>
                    {mip.name}
                  </Option>
                ))}
              </Select>
            </li>

            {/* Role Dropdown */}
            <li style={{ position: "relative" }}>
              <Select
                value={authUser?.role}
                onChange={handleRoleChange}
                style={{ width: 200 }}
                placeholder="Select Role"
              >
                {authUser?.roles.map((role) => (
                  <Option key={role.name} value={role.name}>
                    {role.name}
                  </Option>
                ))}
              </Select>
              {loading && <Spin className="gx-loader-view" />}
            </li>
            <li>
              <StatusColors data={data} />
            </li>
          </div>
        </ul>
      </Header>
    </Auxiliary>
  );
};

export default Topbar;